import React, { useEffect, useState } from "react";
import Form from "@rjsf/mui";
import { customizeValidator } from "@rjsf/validator-ajv8";
import { Box, Button, Typography, Paper, Alert, CircularProgress } from "@mui/material";
import { PageLayout } from "components/Page";
import { makeApiRequest } from "components/utils";

const validator = customizeValidator();

const schema = {
  type: "object",
  required: ["version", "flags"],
  properties: {
    version: { type: "string", title: "Version", default: "2" },
    flags: {
      type: "object",
      title: "Flags",
      additionalProperties: {
        type: "object",
        properties: {
          variants: {
            type: "object",
            title: "Variants",
            additionalProperties: {
              type: "number",
              minimum: 0,
              maximum: 1
            }
          }
        }
      }
    }
  }
};

const uiSchema = {
  "ui:submitButtonOptions": {
    norender: true
  }
};

export const FeatureFlagsEditor = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const json = await makeApiRequest({
          endpoint: "/api/feature-flags",
          method: "GET"
        });
        setData(json);
      } catch (err) {
        setError(err.message || "Failed to load feature flags");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSave = async () => {
    if (!data) { return; }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await makeApiRequest({
        endpoint: "/api/feature-flags",
        method: "POST",
        json: true,
        body: data
      });
      setSuccess("Feature flags saved successfully!");
    } catch (err) {
      setError(err.message || "Failed to save feature flags");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <CircularProgress />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Feature Flags Editor</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      <Alert severity="warning" sx={{ mb: 2 }}>
        This is only for feature flags configured without Optimizely
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Form
          schema={schema}
          uiSchema={uiSchema}
          validator={validator}
          formData={data}
          onChange={(e) => setData(e.formData)}
        />
      </Paper>
    </PageLayout>
  );
};
